import React, { createContext, useContext, useState } from 'react';
import { api } from '../services/api';

const ToastContext = createContext();

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  // Toast utility functions
  const addToast = (type, title, message, duration = 5000, progress = null) => {
    const id = Date.now() + Math.random();
    const toast = { id, type, title, message, duration, collapsed: false, progress };
    setToasts(prev => [...prev, toast]);
    
    if (duration > 0) {
      setTimeout(() => removeToast(id), duration);
    }
    
    return id; // Return the toast ID
  };

  const removeToast = (id) => {
    console.log('Attempting to remove toast with ID:', id);
    setToasts(prev => {
      const newToasts = prev.filter(toast => toast.id !== id);
      console.log('Previous toasts:', prev.length, 'New toasts:', newToasts.length);
      return newToasts;
    });
  };

  const toggleToastCollapse = (id) => {
    setToasts(prev => prev.map(toast => 
      toast.id === id ? { ...toast, collapsed: !toast.collapsed } : toast
    ));
  };

  const showProgressToast = (title, message, progress = 0) => {
    return addToast('info', title, message, 0, progress); // No auto-dismiss for progress toasts
  };

  const updateProgressToast = (toastId, type, title, message, progress = null) => {
    setToasts(prev => prev.map(toast => 
      toast.id === toastId ? { ...toast, type, title, message, progress } : toast
    ));
  };

  const clearAllToasts = () => {
    setToasts([]);
  };

  // Create a self-contained progress toast that manages its own polling
  const createProgressToast = (taskId, initialTitle, initialMessage, onComplete, onError) => {
    const id = Date.now() + Math.random();
    const toast = { 
      id, 
      type: 'info', 
      title: initialTitle, 
      message: initialMessage, 
      duration: 0, 
      collapsed: false, 
      progress: 0,
      taskId,
      onComplete,
      onError,
      isProgressToast: true
    };
    
    setToasts(prev => [...prev, toast]);
    
    // Start polling for this toast
    const pollTaskProgress = async () => {
      try {
        const taskStatus = await api.checkTaskStatus(taskId);
        console.log('Task status for', taskId, ':', taskStatus);
        
        // Update the toast with new progress
        setToasts(prev => prev.map(t => 
          t.id === id ? {
            ...t,
            message: taskStatus.message || t.message,
            progress: taskStatus.progress_percentage || 0
          } : t
        ));
        
        // Check if progress is 100% - task is complete
        if (taskStatus.progress_percentage === 100) {
          // Remove this progress toast
          setToasts(prev => prev.filter(t => t.id !== id));
          
          // Call completion callback
          if (onComplete) {
            onComplete(taskStatus);
          }
          
          return; // Stop polling
        }
        
        // Check if task is completed (status-based completion)
        if (taskStatus.status === 'COMPLETED') {
          // Remove this progress toast
          setToasts(prev => prev.filter(t => t.id !== id));
          
          // Call completion callback
          if (onComplete) {
            onComplete(taskStatus);
          }
          
          return; // Stop polling
        }
        
        // Check if task failed
        if (taskStatus.status === 'FAILED') {
          // Remove this progress toast
          setToasts(prev => prev.filter(t => t.id !== id));
          
          // Call error callback
          if (onError) {
            onError(taskStatus.error || 'Unknown error');
          }
          
          return; // Stop polling
        }
        
        // Continue polling every 10 seconds
        setTimeout(pollTaskProgress, 10000);
        
      } catch (error) {
        console.error('Error polling task status:', error);
        
        // Remove this progress toast
        setToasts(prev => prev.filter(t => t.id !== id));
        
        // Call error callback
        if (onError) {
          onError(error.message);
        }
      }
    };
    
    // Start polling after 10 seconds to get initial status
    setTimeout(pollTaskProgress, 10000);
    
    return id;
  };

  const value = {
    toasts,
    addToast,
    removeToast,
    toggleToastCollapse,
    showProgressToast,
    updateProgressToast,
    clearAllToasts,
    createProgressToast
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
    </ToastContext.Provider>
  );
}; 